/**
 * /people-review — settle the doubtful matches, one card at a time (M54).
 *
 * Evaluation cards: A-B Tech skill passes the import would not attach on its
 * own, with the people they might belong to. Attach them to one, add the
 * evaluated person as someone new, or set the card aside.
 * Tracker cards: a person in the System tracker whose name is already in the
 * System. Same person (they take the tracker's A-B Tech ID / email where
 * theirs is missing), a different person (add them, awaiting intake), or set
 * aside.
 * Dates of birth are never shown — each possible match says how the two
 * dates compare. Every decision is recorded.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import { withLane } from '../lib/lane.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import LanePicker from '../components/LanePicker.jsx';

export const REASONS = {
  dob_differs: 'The name matches, the birth date does not',
  first_name_differs: 'Last name and birth date match, the first name does not',
  last_name_differs: 'First name and birth date match, the last name does not — a name change?',
  two_people: 'Two people share this last name and birth date',
  no_match: 'Nobody in the System matches',
};
const CAME_FROM = { intake: 'System intake', evaluation: 'skill evaluations', tracker: 'System tracker' };
const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });
const day = (d) => (d ? DAY.format(new Date(`${d}T00:00:00Z`)) : '');

function Candidate({ c, children }) {
  return (
    <li className="review-candidate">
      <div>
        <strong>{c.name}</strong> · {c.agency ?? 'no agency'} · {c.level ?? 'level unknown'}
        <div className="muted">
          {c.abtech_id ? `A-B Tech ID ${c.abtech_id}` : 'no A-B Tech ID'}
          {c.cert_number ? ` · cert ${c.cert_number}` : ''}
          {c.email ? ` · ${c.email}` : ''} · came in through {CAME_FROM[c.came_from] ?? c.came_from} · {c.passes} skill passes on record
        </div>
        {c.dob && (
          <div>
            <span className={c.dob === 'same birth date' ? 'badge badge--published' : 'badge badge--warn'}>{c.dob}</span>
          </div>
        )}
      </div>
      <div>{children}</div>
    </li>
  );
}

export function PeopleReviewPage() {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState([]);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(withLane('/api/v1/people-review')));
      setError(null);
    } catch (err) {
      setError(err);
      setData(null);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function rule(kind, card, action, rosterId = null, words) {
    setBusy(`${kind}:${card.key}`);
    setError(null);
    try {
      const out = await apiFetch('/api/v1/people-review/resolve', {
        method: 'POST',
        body: JSON.stringify({ tenant: data.tenant.id, kind, key: card.key, action, roster_id: rosterId }),
      });
      setDone((d) => [{ name: card.name, words, passes: out?.passes_attached ?? 0 }, ...d]);
      setData((prev) => ({ ...prev, [kind]: prev[kind].filter((x) => x.key !== card.key) }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }

  if (data === undefined) return <p className="muted">Loading…</p>;

  return (
    <>
      <div className="page-head">
        <h1>Review matches</h1>
        {data && <LanePicker tenant={data.tenant} tenants={data.tenants} onChange={() => load()} />}
      </div>
      <ErrorBanner error={error} />
      {data && !data.available && (
        <p className="muted">Nothing to review in this lane. The review is for the real System&apos;s people.</p>
      )}
      {data && data.available && (
        <>
          <p className="muted">
            {data.evaluation.length} evaluated {data.evaluation.length === 1 ? 'person' : 'people'} and {data.tracker.length} tracker{' '}
            {data.tracker.length === 1 ? 'entry' : 'entries'} to settle. Birth dates are never shown — each possible match says how the two
            dates compare. Every decision is recorded. Back to <Link to="/personnel">System Personnel</Link>.
          </p>

          {done.length > 0 && (
            <section className="panel panel--quiet" aria-labelledby="done-heading">
              <h2 id="done-heading">Settled just now ({done.length})</h2>
              <ul>
                {done.map((d, i) => (
                  <li key={i} role="status">
                    {d.name} — {d.words}
                    {d.passes ? ` (${d.passes} skill passes attached)` : ''}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="panel" aria-labelledby="eval-heading">
            <h2 id="eval-heading">Skill evaluations ({data.evaluation.length})</h2>
            {data.evaluation.length === 0 && <p className="muted">All settled.</p>}
            {data.evaluation.map((card) => {
              const working = busy === `evaluation:${card.key}`;
              return (
                <article key={card.key} className="review-card" aria-label={`Evaluated: ${card.name}`}>
                  <h3>
                    {card.name} <span className="muted">· {card.agency ?? 'no agency'} · {card.level ?? 'level unknown'}</span>
                  </h3>
                  <p>
                    <span className="badge badge--warn">{REASONS[card.reason] ?? card.reason}</span>
                  </p>
                  <p className="muted">
                    {card.passes} skill {card.passes === 1 ? 'pass' : 'passes'} ({(card.skills ?? []).join(', ')}), evaluated{' '}
                    {card.first_eval === card.last_eval ? day(card.first_eval) : `${day(card.first_eval)} – ${day(card.last_eval)}`}.
                  </p>
                  <p>Is this one of these people?</p>
                  <ul className="review-candidates">
                    {card.candidates.map((c) => (
                      <Candidate key={c.roster_id} c={c}>
                        <button
                          type="button"
                          className="btn btn--auto"
                          disabled={working}
                          onClick={() => rule('evaluation', card, 'attach', c.roster_id, `passes attached to ${c.name}`)}
                        >
                          Same person — attach the {card.passes === 1 ? 'pass' : 'passes'}
                        </button>
                      </Candidate>
                    ))}
                  </ul>
                  <p className="toolbar">
                    <button
                      type="button"
                      className="btn btn--quiet btn--auto"
                      disabled={working}
                      onClick={() => rule('evaluation', card, 'new', null, 'added as a new person, awaiting intake')}
                    >
                      None of them — add {card.name} as a new person
                    </button>
                    <button
                      type="button"
                      className="btn btn--quiet btn--auto"
                      disabled={working}
                      onClick={() => rule('evaluation', card, 'dismiss', null, 'set aside')}
                    >
                      Set aside
                    </button>
                  </p>
                </article>
              );
            })}
          </section>

          <section className="panel" aria-labelledby="tracker-heading">
            <h2 id="tracker-heading">System tracker ({data.tracker.length})</h2>
            <p className="muted">
              In the tracker, but not matched by A-B Tech ID or email — and the name is already in the System. Choosing the same person
              gives them the tracker&apos;s A-B Tech ID and email only where theirs is missing; an intake&apos;s details always stand.
            </p>
            {data.tracker.length === 0 && <p className="muted">All settled.</p>}
            {data.tracker.map((card) => {
              const working = busy === `tracker:${card.key}`;
              return (
                <article key={card.key} className="review-card" aria-label={`Tracker: ${card.name}`}>
                  <h3>
                    {card.name} <span className="muted">· {card.agency ?? 'no agency'} · {card.level ?? 'level unknown'}</span>
                  </h3>
                  <p className="muted">
                    A-B Tech ID {card.abtech_id}
                    {(card.emails ?? []).length ? ` · ${card.emails.join(', ')}` : ''}
                  </p>
                  <ul className="review-candidates">
                    {(card.candidates ?? []).map((c) => (
                      <Candidate key={c.roster_id} c={c}>
                        <button
                          type="button"
                          className="btn btn--auto"
                          disabled={working}
                          onClick={() => rule('tracker', card, 'same', c.roster_id, `same person as ${c.name}`)}
                        >
                          Same person
                        </button>
                      </Candidate>
                    ))}
                  </ul>
                  <p className="toolbar">
                    <button
                      type="button"
                      className="btn btn--quiet btn--auto"
                      disabled={working}
                      onClick={() => rule('tracker', card, 'new', null, 'added as a different person, awaiting intake')}
                    >
                      Different person — add them
                    </button>
                    <button
                      type="button"
                      className="btn btn--quiet btn--auto"
                      disabled={working}
                      onClick={() => rule('tracker', card, 'dismiss', null, 'set aside')}
                    >
                      Set aside
                    </button>
                  </p>
                </article>
              );
            })}
          </section>
        </>
      )}
    </>
  );
}

export default PeopleReviewPage;
